/* One form for every DocType.
 *
 * What it has to get right, in the order people notice when it does not:
 *
 *   1. **Say what is wrong, where it is wrong.** A server validation error
 *      lands on the field that caused it. "Could not save" at the top of a
 *      267-field invoice is the reason people give up on ERPs.
 *   2. **Only send what changed.** The whole document going back is how one
 *      person's edit silently overwrites another's, and how a masked value
 *      gets written over the real one.
 *   3. **Never lose typing.** A failed save leaves the form exactly as it
 *      was, with the values still in it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Conflicted, Failed, Loading, NoAccess } from '@jackdesk/shell'
import { Activity } from './Activity'
import { Emails } from './Emails'
import { PrintDialog } from './PrintDialog'
import { ErpError, newIdempotencyKey } from './api'
import { useErp } from './context'
import { buildRegistry, renderWith } from './controls'
import { evaluateCondition } from './depends'
import { buildLayout, currencyFieldOf } from './layout'
import { applyFetches, fetchesFor } from './fetchFrom'
import { forgetDraft, readDraft, writeDraft } from './drafts'
import type { Doc, DocField, DocMeta } from './types'
import { DOCSTATUS_LABEL, docstatusChip } from './format'

type Props = {
  doctype: string
  /** A name, or 'new' for a document that does not exist yet. */
  name: string
  onSaved?: (doc: Doc) => void
  onOpen?: (doctype: string, name: string) => void
  /** Open a new form pre-filled from this one. */
  onDuplicate?: (doctype: string, values: Doc) => void
  onDeleted?: (doctype: string) => void
  header?: ReactNode
  /** Rendered under the fields, above the activity. Where a DocType earns a
   *  hand-built panel -- a bill of materials' tree and costing, say -- it goes
   *  here rather than replacing the generic form, so every other field on the
   *  document still draws itself. Design §12 calls this tier 2. */
  below?: (doc: Doc) => ReactNode
  /** Values to start a new document from, for Duplicate. */
  initial?: Doc
}

export function FormView({ doctype, name, onSaved, onOpen, onDuplicate, onDeleted, header, below, initial }: Props) {
  const erp = useErp()
  const isNew = name === 'new'

  const [meta, setMeta] = useState<DocMeta | null>(null)
  const [doc, setDoc] = useState<Doc | null>(null)
  const [docinfo, setDocinfo] = useState<Record<string, unknown>>({})
  const [changes, setChanges] = useState<Doc>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [banner, setBanner] = useState<ErpError | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<false | 'save' | 'submit' | 'cancel'>(false)
  const [tab, setTab] = useState(0)
  const [printing, setPrinting] = useState(false)
  const [restored, setRestored] = useState(0)
  const [filling, setFilling] = useState<string | null>(null)
  const [steps, setSteps] = useState<{ action?: string; next_state?: string }[]>([])
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const firstError = useRef<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setBanner(null)
    try {
      // Both at once: reading the document never needed the metadata, but
      // waiting for it anyway cost a whole round trip -- about 180ms of a
      // 1000ms budget on a 4G link, for nothing.
      const [m, r] = await Promise.all([
        erp.meta(doctype),
        isNew ? Promise.resolve(null) : erp.read(doctype, name),
      ])
      setMeta(m)
      if (m.no_access) { setDoc(null); return }
      if (isNew) {
        setDocinfo({})
        const blank: Doc = { doctype, docstatus: 0 }
        for (const f of m.fields) if (f.default && f.default !== 'Today') blank[f.fieldname] = f.default
        setDoc(blank)
        // Duplicate hands the copied values in; they count as unsaved
        // changes so the form is dirty and Save is live straight away.
        setChanges(initial ? { ...initial } : {})
      } else if (r) {
        setDoc(r.doc); setDocinfo(r.docinfo ?? {})
        // Anything typed and not saved before the tab closed. Only restored
        // when it was typed against the version still on screen: offering
        // edits made against an older copy would silently undo somebody
        // else's work.
        const draft = readDraft(doctype, name)
        if (draft && (!draft.seen || draft.seen === String(r.doc.modified ?? ''))) {
          setChanges(draft.changes)
          setRestored(Object.keys(draft.changes).length)
        } else {
          if (draft) forgetDraft(doctype, name)
          setChanges({})
        }
      }
    } catch (e) {
      setBanner(e as ErpError)
    } finally {
      setLoading(false)
    }
  }, [erp, doctype, name, isNew, initial])

  useEffect(() => { void load() }, [load])

  // What the organisation's workflow allows on this document. Most DocTypes
  // have none, and that comes back as an empty list rather than an error.
  useEffect(() => {
    if (isNew || !doc?.name) { setSteps([]); return }
    const controller = new AbortController()
    erp.transitions(doctype, String(doc.name), controller.signal)
      .then((r) => setSteps(r.transitions ?? []))
      .catch(() => setSteps([]))
    return () => controller.abort()
  }, [erp, doctype, doc?.name, doc?.modified, isNew])

  const childFields = useCallback(
    (child: string) => meta?.children?.[child]?.fields ?? [],
    [meta],
  )
  const registry = useMemo(() => buildRegistry(childFields), [childFields])

  const merged = useMemo<Doc>(() => ({ ...(doc ?? {}), ...changes }), [doc, changes])
  const layout = useMemo(() => buildLayout(meta?.fields ?? []), [meta])

  const submitted = merged.docstatus === 1
  const cancelled = merged.docstatus === 2
  const canWrite = Boolean(meta?.can.write) && !cancelled
  const dirty = Object.keys(changes).length > 0

  // Kept as they type, so a closed tab does not cost an hour of entry.
  useEffect(() => {
    if (isNew || !doc?.name) return
    writeDraft(doctype, String(doc.name), changes, String(doc.modified ?? ''))
  }, [doctype, doc?.name, doc?.modified, changes, isNew])

  // Leaving with unsaved work warns (F06). The browser's own dialog, because
  // a custom one cannot stop navigation.
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  /** Re-read just the document's trimmings after a comment, tag or
   *  assignment. Reloading the whole form would throw away anything the
   *  person had typed and not yet saved. */
  const refreshActivity = useCallback(async () => {
    if (isNew || !doc?.name) return
    try {
      const r = await erp.read(doctype, String(doc.name))
      setDocinfo(r.docinfo ?? {})
    } catch { /* the panel keeps what it has; the document is unaffected */ }
  }, [erp, doctype, doc?.name, isNew])

  const set = (fieldname: string, value: unknown) => {
    setChanges((c) => ({ ...c, [fieldname]: value }))
    setErrors((e) => (e[fieldname] ? { ...e, [fieldname]: '' } : e))
    void fill(fieldname, value)
  }

  /**
   * Fill the fields that read off this link.
   *
   * The server applies `fetch_from` on save, so the value is right in the
   * end either way. What is missing without this is the feedback: you pick a
   * customer and the name, tax id and currency sit blank until you save,
   * which reads as a form that is not working.
   */
  const fill = useCallback(async (fieldname: string, value: unknown) => {
    if (!meta) return
    const field = meta.fields.find((f) => f.fieldname === fieldname)
    if (!field || (field.fieldtype !== 'Link' && field.fieldtype !== 'Dynamic Link')) return
    const fetches = fetchesFor(meta.fields, fieldname)
    if (fetches.length === 0) return

    const target = field.fieldtype === 'Link'
      ? field.options
      : String((merged as Record<string, unknown>)[field.options ?? ''] ?? '')
    if (!target) return

    if (!value) {
      // Cleared: clear what came from it, rather than leaving a name beside
      // an empty customer.
      setChanges((c) => ({ ...c, ...Object.fromEntries(fetches.map((f) => [f.field, null])) }))
      return
    }

    setFilling(fieldname)
    try {
      const linked = await erp.read(target, String(value))
      const filled = applyFetches(fetches, linked.doc, { ...merged, [fieldname]: value })
      if (Object.keys(filled).length) setChanges((c) => ({ ...c, ...filled }))
    } catch {
      // The server will fill them on save. A failure here is a missing
      // convenience, not a broken form, so it says nothing.
    } finally {
      setFilling(null)
    }
  }, [erp, meta, merged])

  const fieldState = (f: DocField) => {
    const show = evaluateCondition(f.depends_on, merged)
    const required = Boolean(f.reqd) || evaluateCondition(f.mandatory_depends_on, merged, false)
    const readOnly = !canWrite
      || Boolean(f.read_only)
      || evaluateCondition(f.read_only_depends_on, merged, false)
      || (submitted && !f.allow_on_submit)
    return { show, required, readOnly }
  }

  const save = useCallback(async () => {
    if (!meta || !doc) return
    // Required fields are checked here as well as on the server, so the
    // person is told before a round trip rather than after one.
    const missing: Record<string, string> = {}
    for (const f of meta.fields) {
      const { show, required } = fieldState(f)
      if (!show || !required) continue
      const v = merged[f.fieldname]
      if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
        missing[f.fieldname] = `${f.label || f.fieldname} is needed.`
      }
    }
    if (Object.keys(missing).length) {
      setErrors(missing)
      firstError.current = Object.keys(missing)[0] ?? null
      focusFirstError(Object.keys(missing)[0])
      return
    }

    setBusy('save'); setBanner(null); setErrors({})
    try {
      const key = newIdempotencyKey()
      const saved = isNew
        ? await erp.create(doctype, merged, key)
        : await erp.update(doctype, String(doc.name), changes, String(doc.modified ?? ''), key)
      setDoc(saved); setChanges({}); setRestored(0)
      if (!isNew && doc.name) forgetDraft(doctype, String(doc.name))
      onSaved?.(saved)
    } catch (e) {
      const error = e as ErpError
      setBanner(error)
      if (Object.keys(error.fields).length && error.code !== 'conflict') {
        setErrors(Object.fromEntries(Object.entries(error.fields).map(([k, v]) => [k, String(v)])))
        focusFirstError(Object.keys(error.fields)[0])
      }
    } finally {
      setBusy(false)
    }
  }, [erp, meta, doc, changes, isNew, doctype, name, onSaved])

  // Ctrl+S saves, because Save is *above* the fields in the page and a
  // keyboard user who has just typed into one cannot Tab forward to it.
  // Found by the journey that drives a whole invoice without a mouse.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 's' || !(e.metaKey || e.ctrlKey)) return
      e.preventDefault()
      if (!busy && dirty) void save()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [save, busy, dirty])

  const act = async (verb: 'submit' | 'cancel') => {
    if (!doc?.name) return
    setBusy(verb); setBanner(null)
    try {
      const result = await erp.action(doctype, String(doc.name), verb, newIdempotencyKey())
      // Frappe's submit/cancel answer is the document; re-read anyway when it
      // is not, so the screen never shows a stale docstatus.
      if (result && typeof result === 'object' && 'docstatus' in result) setDoc(result as Doc)
      else await load()
      setChanges({})
    } catch (e) {
      setBanner(e as ErpError)
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <div className="page"><Loading rows={8} label={`Loading ${doctype}`} /></div>
  if (meta?.no_access) return <div className="page"><NoAccess what={meta.label ?? doctype} needs={meta.no_access.needs} /></div>
  if (banner && !doc) return <div className="page"><Failed message={banner.message} requestId={banner.requestId} onRetry={load} /></div>
  if (!meta || !doc) return <div className="page"><Failed onRetry={load} /></div>

  const title = (meta.title_field && merged[meta.title_field]) || merged.name || `New ${meta.label ?? doctype}`

  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      {header}
      <div className="row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          {/* A Single's one record is named after its DocType, so the eyebrow
              and the heading would say the same words twice. */}
          {meta.issingle ? null : <div className="small muted">{meta.label ?? doctype}</div>}
          <h1 style={{ marginTop: 2 }}>{meta.issingle ? (meta.label ?? doctype) : String(title)}</h1>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {meta.is_submittable ? (
            <span className={`chip ${docstatusChip(merged.docstatus)}`}>
              {DOCSTATUS_LABEL[(merged.docstatus ?? 0) as 0 | 1 | 2]}
            </span>
          ) : null}
          {dirty ? <span className="chip chip-warn">Unsaved changes</span> : null}
          {canWrite ? (
            <button className="btn btn-primary" onClick={save} disabled={busy !== false || (!dirty && !isNew)}>
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
          ) : null}
          {meta.is_submittable && !isNew && merged.docstatus === 0 && meta.can.submit ? (
            <button className="btn" onClick={() => act('submit')} disabled={busy !== false || dirty}
                    title={dirty ? 'Save first' : undefined}>
              {busy === 'submit' ? 'Submitting…' : 'Submit'}
            </button>
          ) : null}
          {/* Not offered on a cancelled document: ERPNext refuses to print
              one ("Not allowed to print cancelled documents"), and a button
              that always fails is worse than no button. */}
          {!isNew && !cancelled ? (
            <NextSteps doctype={doctype} name={String(doc.name)}
                       onMake={(target, values) => onDuplicate?.(target, values)} />
          ) : null}
          {!isNew ? (
            <DocumentMenu doctype={doctype} name={String(doc.name)} meta={meta}
                          onDuplicated={(values) => onDuplicate?.(doctype, values)}
                          onRenamed={(to) => onSaved?.({ ...doc, name: to })}
                          onDeleted={() => onDeleted?.(doctype)} />
          ) : null}
          {!isNew && meta.can.print && !cancelled ? (
            <PrintButton doctype={doctype} name={String(doc.name)}
                         busy={printing} setBusy={setPrinting} />
          ) : null}
          {steps.map((s) => (
            <button key={s.action} className="btn" disabled={busy !== false || dirty}
                    title={dirty ? 'Save first' : `Moves it to ${s.next_state}`}
                    onClick={async () => {
                      setBusy('save')
                      try { setDoc(await erp.applyWorkflow(doctype, String(doc.name), s.action!)); setChanges({}) }
                      catch (e) { setBanner(e as ErpError) }
                      finally { setBusy(false) }
                    }}>{s.action}</button>
          ))}
          {cancelled && meta.can.amend ? (
            <button className="btn" disabled={busy !== false}
                    title="Make a fresh draft that points back at this one"
                    onClick={async () => {
                      setBusy('save')
                      try {
                        const fresh = await erp.amend(doctype, String(doc.name))
                        onSaved?.(fresh)
                      } catch (e) { setBanner(e as ErpError) }
                      finally { setBusy(false) }
                    }}>Amend</button>
          ) : null}
          {meta.is_submittable && merged.docstatus === 1 && meta.can.cancel ? (
            <button className="btn btn-danger" onClick={() => act('cancel')} disabled={busy !== false}>
              {busy === 'cancel' ? 'Cancelling…' : 'Cancel'}
            </button>
          ) : null}
        </div>
      </div>

      {restored > 0 ? (
        <div className="card card-pad row" role="status"
             style={{ gap: 10, flexWrap: 'wrap', borderLeft: '3px solid var(--warn)' }}>
          <span className="small">
            {restored === 1 ? 'One change you' : `${restored} changes you`} had not saved
            {restored === 1 ? ' was' : ' were'} put back.
          </span>
          <span className="grow" />
          <button className="btn btn-sm" onClick={() => {
            setChanges({}); setRestored(0)
            if (doc.name) forgetDraft(doctype, String(doc.name))
          }}>Discard them</button>
        </div>
      ) : null}

      {banner ? (
        banner.code === 'conflict'
          ? <Conflicted fields={banner.conflictedFields} onReload={load} />
          : <div className="card card-pad" role="alert" style={{ borderLeft: '3px solid var(--danger)' }}>
              <strong>{banner.message}</strong>
              {banner.requestId ? <div className="small muted mono">{banner.requestId}</div> : null}
            </div>
      ) : null}

      {layout.length > 1 ? (
        <div className="row" style={{ gap: 4, borderBottom: '1px solid var(--line)', overflowX: 'auto' }}>
          {layout.map((t, i) => (
            <button key={t.label + i} className="btn btn-quiet"
                    aria-current={i === tab ? 'true' : undefined}
                    style={i === tab ? { borderBottom: '2px solid var(--accent)', borderRadius: 0, color: 'var(--accent)' } : { borderRadius: 0 }}
                    onClick={() => setTab(i)}>{t.label}</button>
          ))}
        </div>
      ) : null}

      {(layout[tab]?.sections ?? []).map((section, si) => {
        if (!evaluateCondition(section.showWhen, merged)) return null
        const key = `${tab}:${si}`
        const shut = collapsed[key] ?? (section.collapsible && !evaluateCondition(section.collapseWhen, merged, false))
        return (
          <section key={key} className="card card-pad">
            {section.label ? (
              <button className="row-between" style={{ width: '100%', background: 'none', border: 0, padding: 0, cursor: section.collapsible ? 'pointer' : 'default', color: 'inherit', font: 'inherit', marginBottom: shut ? 0 : 14 }}
                      onClick={() => section.collapsible && setCollapsed((c) => ({ ...c, [key]: !shut }))}
                      aria-expanded={section.collapsible ? !shut : undefined}>
                <h3>{section.label}</h3>
                {section.collapsible ? <span className="muted small">{shut ? 'Show' : 'Hide'}</span> : null}
              </button>
            ) : null}
            {shut ? null : (
              <div style={{ display: 'grid', gap: 16, gridTemplateColumns: `repeat(${Math.min(section.columns.length, 3)}, minmax(0, 1fr))` }}
                   className="erp-cols">
                {section.columns.map((col, ci) => (
                  <div key={ci} className="stack">
                    {col.fields.map((f) => {
                      const { show, required, readOnly } = fieldState(f)
                      if (!show) return null
                      const currencyField = f.fieldtype === 'Currency' ? currencyFieldOf(f) : null
                      return (
                        <div key={f.fieldname} data-field={f.fieldname}>
                          {renderWith(registry, {
                            field: f, value: merged[f.fieldname], doc: merged,
                            readOnly, required, error: errors[f.fieldname] || undefined,
                            currency: currencyField ? String(merged[currencyField] ?? 'INR') : undefined,
                            onChange: (v) => set(f.fieldname, v),
                          })}
                        </div>
                      )
                    })}
                  </div>
                ))}
              </div>
            )}
          </section>
        )
      })}

      {isNew || !below ? null : below(doc)}

      {isNew ? null : (
        <Emails doctype={doctype} name={String(doc.name)}
                suggestSubject={`${meta.label || doctype} ${doc.name}`} />
      )}

      {isNew ? null : (
        <Activity doctype={doctype} name={String(doc.name)} docinfo={docinfo}
                  canWrite={Boolean(meta.can.read)} onChanged={() => void refreshActivity()} />
      )}
    </div>
  )
}

/**
 * Print, with the format picker behind it.
 *
 * Most people want "the invoice" and one click; the few who want the credit
 * note layout or a different letterhead should not have to leave the
 * document to get it. So the button prints the default and the caret opens
 * the rest — and the PDF is fetched rather than linked, because a plain link
 * cannot carry the headers that say who is asking.
 */
function PrintButton({ doctype, name, busy }: {
  doctype: string; name: string; busy: boolean; setBusy: (v: boolean) => void
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button className="btn" onClick={() => setOpen(true)} disabled={busy}>Print</button>
      {open ? <PrintDialog doctype={doctype} name={name} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

/**
 * What you make next: Deliver these goods, Bill the customer, Record the money.
 *
 * The spine of every trading cycle. ERPNext builds the next document as a
 * draft with the lines already carried across, and this opens it as a form
 * rather than saving it — so the person sees what came over before it is
 * real, which is the only way anybody notices a quantity they did not mean.
 */
function NextSteps({ doctype, name, onMake }: {
  doctype: string; name: string; onMake: (target: string, values: Doc) => void
}) {
  const erp = useErp()
  const [steps, setSteps] = useState<{ target: string; label: string; ready: boolean }[]>([])
  const [whyNot, setWhyNot] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    erp.nextSteps(doctype, name, controller.signal)
      .then((r) => { setSteps(r.next ?? []); setWhyNot(r.why_not ?? null) })
      .catch(() => setSteps([]))
    return () => controller.abort()
  }, [erp, doctype, name])

  if (steps.length === 0) return null

  return (
    <div style={{ position: 'relative' }}>
      <button className="btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}
              disabled={busy !== null}>
        {busy ? 'Preparing…' : 'Create'} ▾
      </button>
      {open ? (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 30,
                                         minWidth: 240, padding: 4, boxShadow: 'var(--shadow-2)' }}>
            {whyNot ? <div className="small muted" style={{ padding: 8 }}>{whyNot}</div> : null}
            {steps.map((s) => (
              <button key={s.target} className="navitem" disabled={!s.ready}
                      style={!s.ready ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      onClick={async () => {
                        setBusy(s.target); setFailed(null); setOpen(false)
                        try {
                          const made = await erp.makeNext(doctype, name, s.target)
                          onMake(made.doctype, made.values)
                        } catch (e) { setFailed((e as ErpError).message) }
                        finally { setBusy(null) }
                      }}>
                <span className="grow truncate">{s.label}</span>
                <span className="small muted">{s.target}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}
      {failed ? <div className="small err" style={{ position: 'absolute', right: 0, top: '100%', whiteSpace: 'nowrap', zIndex: 31 }}>{failed}</div> : null}
    </div>
  )
}

/** Duplicate, rename and delete — the things you do to a document rather
 *  than in it. Behind a menu because they are rare and one of them cannot be
 *  undone. */
function DocumentMenu({ doctype, name, meta, onDuplicated, onRenamed, onDeleted }: {
  doctype: string; name: string; meta: DocMeta
  onDuplicated: (values: Doc) => void
  onRenamed: (to: string) => void
  onDeleted: () => void
}) {
  const erp = useErp()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const can = meta.can
  if (!can.create && !can.delete && !meta.allow_rename) return null

  const run = async (what: () => Promise<void>) => {
    setBusy(true); setFailed(null); setOpen(false)
    try { await what() } catch (e) { setFailed((e as ErpError).message) } finally { setBusy(false) }
  }

  return (
    <div style={{ position: 'relative' }}>
      <button className="btn" aria-label="More actions" aria-expanded={open} disabled={busy}
              onClick={() => setOpen((v) => !v)}>⋯</button>
      {open ? (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 29 }} onClick={() => setOpen(false)} />
          <div className="card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 30,
                                         minWidth: 190, padding: 4, boxShadow: 'var(--shadow-2)' }}>
            {can.create ? (
              <button className="navitem" onClick={() => void run(async () => {
                const copy = await erp.duplicate(doctype, name)
                onDuplicated(copy.values)
              })}>Duplicate</button>
            ) : null}
            {meta.allow_rename && can.write ? (
              <button className="navitem" onClick={() => void run(async () => {
                const to = window.prompt(`Rename ${name} to what?`, name)?.trim()
                if (!to || to === name) return
                const answer = await erp.renameDoc(doctype, name, to)
                onRenamed(answer.name)
              })}>Rename…</button>
            ) : null}
            {can.delete ? (
              <button className="navitem" style={{ color: 'var(--danger)' }}
                      onClick={() => void run(async () => {
                        if (!window.confirm(`Delete ${name}? This cannot be undone.`)) return
                        await erp.remove(doctype, name)
                        onDeleted()
                      })}>Delete</button>
            ) : null}
          </div>
        </>
      ) : null}
      {failed ? <div className="small err" style={{ position: 'absolute', right: 0, top: '100%', whiteSpace: 'nowrap' }}>{failed}</div> : null}
    </div>
  )
}

function focusFirstError(fieldname?: string) {
  if (!fieldname) return
  window.requestAnimationFrame(() => {
    const holder = document.querySelector(`[data-field="${CSS.escape(fieldname)}"]`)
    holder?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    holder?.querySelector<HTMLElement>('input, select, textarea, [contenteditable]')?.focus()
  })
}
